import { useQuery } from '@tanstack/react-query';
import { supabaseWithAuth } from '@/utils/supabaseWrapper';
import { useAuth } from './useAuth';
import { useOrganizationFeatures } from './useOrganizationFeatures';
import { logger } from '@/utils/productionLogger';

interface RequiredActionsData {
  overdueCampaigns: number;
  fraudAlerts: number;
  unsharedReports: number;
}

/**
 * Hook pour récupérer les compteurs des actions requises
 * - Campagnes hors délais
 * - Campagnes avec suspicion de fraude
 * - Rapports non partagés aux candidats
 * 
 * Adapté au nouveau modèle d'accès par rôle (member/admin/owner)
 * 
 * Utilise React Query pour:
 * - Cache intelligent (30s)
 * - Refresh automatique au focus de la fenêtre
 * - Invalidation manuelle possible
 */
export const useRequiredActions = () => {
  const { profile } = useAuth();
  const { organizationData } = useOrganizationFeatures();

  const userRole = organizationData?.userRole;
  const orgId = profile?.organization_id;

  const fetchRequiredActions = async (): Promise<RequiredActionsData> => {
    if (!orgId) {
      return {
        overdueCampaigns: 0,
        fraudAlerts: 0,
        unsharedReports: 0,
      };
    }

    try {

        // 1. Campagnes hors délais avec protection auth
        // Récupérer toutes les campagnes actives selon le rôle
        const { data: activeCampaigns, error: overdueError } = await supabaseWithAuth.executeWithRetry(async () => {
          let query = supabaseWithAuth
            .from('campaigns')
            .select('id, created_at, campaign_duration, recruiter_id')
            .eq('organization_id', orgId)
            .not('status', 'in', '(Terminé,Annulée,Refusé)');
          
          // member: uniquement ses campagnes
          if (userRole === 'member') {
            query = query.eq('recruiter_id', profile.id);
          }
          
          return query;
        }, 'required_actions.overdue_campaigns');

        if (overdueError) {
          logger.error('Error fetching overdue campaigns', overdueError);
        }

        // Calculer les campagnes hors délais côté client
        const now = Date.now();
        const overdueCampaignsCount = (activeCampaigns || []).filter((campaign: any) => {
          const createdAt = new Date(campaign.created_at).getTime();
          const durationDays = campaign.campaign_duration === '5_days' ? 5 : 3;
          const deadline = createdAt + (durationDays * 24 * 60 * 60 * 1000);
          return now > deadline;
        }).length;

        // 2. Campagnes avec alertes de fraude (campagnes uniques) avec protection auth
        // Support ancien et nouveau système (coexistence)
        const { data: fraudData, error: fraudError } = await supabaseWithAuth.executeWithRetry(async () => {
          return supabaseWithAuth
            .from('fraud_alert_tracking')
            .select('campaign_id')
            .in('alert_type', [
              'referent_email_validation_failed', // Ancien système
              'referent_evaluation_fraud',        // Ancien système
              'referent_unified_fraud_analysis'   // Nouveau système unifié
            ])
            .not('campaign_id', 'is', null);
        }, 'required_actions.fraud_alerts');

        if (fraudError) {
          logger.error('Error fetching fraud alerts', fraudError);
        }

        // Filtrer les campagnes de fraude selon le rôle
        let fraudCampaignIds = fraudData ? (fraudData as any[]).map((item: any) => item.campaign_id) : [];
        
        if (userRole === 'member') {
          // Vérifier que les campagnes appartiennent au membre
          const { data: memberCampaigns } = await supabaseWithAuth.executeWithRetry(async () => {
            return supabaseWithAuth
              .from('campaigns')
              .select('id')
              .eq('recruiter_id', profile.id)
              .in('id', fraudCampaignIds);
          }, 'required_actions.member_fraud_campaigns');
          
          fraudCampaignIds = memberCampaigns ? memberCampaigns.map((c: any) => c.id) : [];
        }

        // Compter les campagnes uniques avec fraude
        const uniqueFraudCampaigns = new Set(fraudCampaignIds).size;

        // 3. Rapports non partagés avec protection auth
        // D'abord, récupérer les campagnes terminées selon le rôle
        const { data: completedCampaigns, error: campaignsError } = await supabaseWithAuth.executeWithRetry(async () => {
          let query = supabaseWithAuth
            .from('campaigns')
            .select('id')
            .eq('organization_id', orgId)
            .eq('status', 'Terminé');
          
          // member: uniquement ses campagnes
          if (userRole === 'member') {
            query = query.eq('recruiter_id', profile.id);
          }
          
          return query;
        }, 'required_actions.completed_campaigns');

        if (campaignsError) {
          logger.error('Error fetching completed campaigns', campaignsError);
        }

        const completedCampaignIds = (completedCampaigns || []).map((c: any) => c.id);

        // Ensuite, récupérer les rapports pour ces campagnes avec protection auth
        const { data: reports, error: reportsError } = await supabaseWithAuth.executeWithRetry(async () => {
          return supabaseWithAuth
            .from('campaign_reports')
            .select('campaign_id')
            .in('campaign_id', completedCampaignIds);
        }, 'required_actions.reports');

        if (reportsError) {
          logger.error('Error fetching reports', reportsError);
        }

        // Enfin, vérifier quels rapports n'ont pas été partagés avec protection auth
        const { data: sharedReports, error: sharedError } = await supabaseWithAuth.executeWithRetry(async () => {
          return supabaseWithAuth
            .from('message_logs')
            .select('campaign_id')
            .in('campaign_id', completedCampaignIds)
            .like('email_type', 'report_shared%')
            .eq('status', 'sent');
        }, 'required_actions.shared_reports');

        if (sharedError) {
          logger.error('Error fetching shared reports', sharedError);
        }

        const sharedCampaignIds = new Set((sharedReports || []).map((r: any) => r.campaign_id));
        const unsharedReportsCount = (reports || []).filter(
          (report: any) => !sharedCampaignIds.has(report.campaign_id)
        ).length;

        return {
          overdueCampaigns: overdueCampaignsCount,
          fraudAlerts: uniqueFraudCampaigns,
          unsharedReports: unsharedReportsCount,
        };

      } catch (error) {
        logger.error('Error in useRequiredActions', error as Error);
        throw error;
      }
    };

  return useQuery({
    queryKey: ['required-actions', profile?.id, orgId, userRole],
    queryFn: fetchRequiredActions,
    enabled: !!profile?.id && !!orgId, // Ne lance la requête que si on a un profil et une org
    staleTime: 30000, // Cache pendant 30 secondes
    refetchOnWindowFocus: true, // Refresh quand on revient sur l'onglet
    retry: 2, // Réessaie 2 fois en cas d'erreur
  });
};
